import { jwtDecode } from 'jwt-decode';
import api from '../axios';

/**
 * Serviço de autenticação com gerenciamento seguro do localStorage
 */
class AuthService {
  
  /**
   * Limpa completamente o localStorage antes da autenticação
   */
  limparLocalStorage() {
    try {
      // Lista todas as chaves existentes no localStorage
      const keys = Object.keys(localStorage);
      
      // Remove cada item individualmente
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Força a limpeza completa
      localStorage.clear();
      
      console.log('localStorage limpo com sucesso');
    } catch (error) {
      console.error('Erro ao limpar localStorage:', error);
    }
  }

  /**
   * Realiza a autenticação do usuário
   * @param {Object} credentials - Credenciais do usuário
   * @param {string} credentials.email - Email do usuário
   * @param {string} credentials.password - Senha do usuário
   * @returns {Promise} Resultado da autenticação
   */
  async autenticar(credentials) {
    try {
      // PASSO 1: Limpa completamente o localStorage antes de autenticar
      this.limparLocalStorage();
      
      // PASSO 2: Realiza a autenticação
      const response = await api.post('/api/Auth/Login', credentials);
      console.log('Resposta da autenticação recebida:', response.data);
      if (response.data?.token) {
        // PASSO 3: Verifica se há variáveis existentes no localStorage
        console.log('Verificando variáveis existentes no localStorage...');
        const variaveisExistentes = this.verificarVariaveisExistentes();

        
        if (variaveisExistentes.length > 0) {
          console.log('Variáveis encontradas no localStorage após autenticação:', variaveisExistentes);
          
          // PASSO 4: Subscreve/sobrescreve os valores existentes
          await this.subscreverVariaveis(variaveisExistentes, response.data);
        }
        
        // PASSO 5: Armazena os dados de autenticação
        this.armazenarDadosAutenticacao(response.data);
        
        // PASSO 6: Procede com os demais passos da aplicação
        await this.inicializarAplicacao(response.data);
        
        return {
          success: true,
          data: response.data,
          message: 'Autenticação realizada com sucesso'
        };
      }
      
      return {
        success: false,
        error: 'Credenciais inválidas'
      };
      
    } catch (error) {
      console.error('Erro durante autenticação:', error);
      
      // Em caso de erro, garante que o localStorage esteja limpo
      this.limparLocalStorage();
      
      return {
        success: false,
        error: error.response?.data?.message || 'Erro ao autenticar usuário'
      };
    }
  }

  /**
   * Verifica se há variáveis existentes no localStorage
   * @returns {Array} Lista de variáveis encontradas
   */
  verificarVariaveisExistentes() {
    const variaveisEncontradas = [];
    
    try {
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        const valor = localStorage.getItem(key);
        if (valor) {
          variaveisEncontradas.push({
            chave: key,
            valor: valor,
            timestamp: Date.now()
          });
        }
      });
      
    } catch (error) {
      console.error('Erro ao verificar variáveis existentes:', error);
    }
    
    return variaveisEncontradas;
  }

  /**
   * Subscreve/sobrescreve variáveis existentes no localStorage
   * @param {Array} variaveisExistentes - Lista de variáveis encontradas
   * @param {Object} dadosAutenticacao - Dados vindos da autenticação
   */
  async subscreverVariaveis(variaveisExistentes, dadosAutenticacao) {
    try {
      console.log('Iniciando subscrição de variáveis...');
      
      // Mapeia as variáveis existentes e atualiza com novos valores se necessário
      const variaveisAtualizadas = variaveisExistentes.map(variavel => {
        // Verifica se a variável tem correspondência nos dados de autenticação
        const novoValor = this.obterNovoValor(variavel.chave, dadosAutenticacao);
        
        return {
          chave: variavel.chave,
          valorAnterior: variavel.valor,
          novoValor: novoValor || variavel.valor,
          atualizada: !!novoValor,
          timestamp: Date.now()
        };
      });
      
      // Aplica as atualizações no localStorage
      variaveisAtualizadas.forEach(variavel => {
        if (variavel.atualizada) {
          localStorage.setItem(variavel.chave, variavel.novoValor);
          console.log(`Variável ${variavel.chave} atualizada de "${variavel.valorAnterior}" para "${variavel.novoValor}"`);
        }
      });
      
      console.log('Subscrição de variáveis concluída');
      
    } catch (error) {
      console.error('Erro ao subscrever variáveis:', error);
    }
  }

  /**
   * Obtém novo valor para uma chave baseado nos dados de autenticação
   * @param {string} chave - Chave da variável
   * @param {Object} dadosAutenticacao - Dados da autenticação
   * @returns {string|null} Novo valor ou null se não houver
   */
  obterNovoValor(chave, dadosAutenticacao) {
    const mapeamento = {
      'token': dadosAutenticacao.token,
      'user_id': dadosAutenticacao.user?.id,
      'username': dadosAutenticacao.user?.username,
      'email': dadosAutenticacao.user?.email,
      'unidade_consumidora': dadosAutenticacao.unidade_consumidora,
      'perfil_usuario': dadosAutenticacao.user?.perfil,
      'last_login': new Date().toISOString(),
      'session_id': dadosAutenticacao.session_id
    };
    
    return mapeamento[chave] || null;
  }

  /**
   * Armazena dados essenciais de autenticação no localStorage
   * @param {Object} dadosAutenticacao - Dados da autenticação
   */
  armazenarDadosAutenticacao(tokensAutenticacao) {
    try {
      // Dados essenciais para manter na sessão
      console.log('Armazenando dados de autenticação no localStorage...');
      // decodificar e armazenar apenas o necessário
      const dadosAutenticacao = jwtDecode(tokensAutenticacao.token);
      console.log('Dados decodificados da autenticação:', dadosAutenticacao);
      const dadosEssenciais = {
        access_token_tarifasmp: tokensAutenticacao.token,
        medidorId: dadosAutenticacao.medidorId,
        unidadeConsumidoraId: dadosAutenticacao.unidadeConsumidoraId,
        clienteNome: dadosAutenticacao.clienteNome,
        sessionId: dadosAutenticacao.sessionId,
        expiresAt: dadosAutenticacao.exp,
        authenticatedAt: new Date().toISOString()
      };
      
      // Armazena cada dado individualmente
      Object.entries(dadosEssenciais).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
      });
      
      console.log('Dados de autenticação armazenados no localStorage');
      
    } catch (error) {
      console.error('Erro ao armazenar dados de autenticação:', error);
    }
  }

  /**
   * Inicializa a aplicação após autenticação bem-sucedida
   * @param {Object} dadosAutenticacao - Dados da autenticação
   */
  async inicializarAplicacao(dadosAutenticacao) {
    try {
      console.log('Inicializando aplicação...');
      
      // Configura o token no header das requisições
      if (dadosAutenticacao.token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${dadosAutenticacao.token}`;
      }
      
      // Carrega dados iniciais se necessário
      await this.carregarDadosIniciais(dadosAutenticacao);
      
      console.log('Aplicação inicializada com sucesso');
      
    } catch (error) {
      console.error('Erro ao inicializar aplicação:', error);
    }
  }

  /**
   * Carrega dados iniciais necessários para a aplicação
   * @param {Object} dadosAutenticacao - Dados da autenticação
   */
  async carregarDadosIniciais(dadosAutenticacao) {
    try {
      // Aqui você pode carregar dados específicos necessários para sua aplicação
      // Por exemplo: configurações do usuário, dados de consumo iniciais, etc.
      
      if (dadosAutenticacao.unidade_consumidora) {
        console.log(`Carregando dados para unidade consumidora: ${dadosAutenticacao.unidade_consumidora}`);
        // Implementar carregamento de dados específicos
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  }

  /**
   * Logout do usuário
   */
  async logout() {
    try {
      // Faz a requisição de logout na API
      await api.post('/api/auth/logout');
      
    } catch (error) {
      console.error('Erro ao fazer logout na API:', error);
    } finally {
      // Sempre limpa o localStorage independente da resposta da API
      this.limparLocalStorage();
      
      // Remove o token do header das requisições
      delete api.defaults.headers.common['Authorization'];
      
      console.log('Logout realizado e localStorage limpo');
    }
  }

  /**
   * Verifica se o usuário está autenticado
   * @returns {boolean} Status de autenticação
   */
  isAuthenticated() {
    try {
      const token = localStorage.getItem('access_token_tarifasmp');
      const expiresAt = localStorage.getItem('expiresAt');
      if (!token || !expiresAt) {
        return false;
      }
      
      // Verifica se o token não expirou
      const agora = Math.floor(Date.now() / 1000); // Converte para timestamp Unix (segundos)
      const expiracaoUnix = parseInt(expiresAt); // expiresAt já está em segundos (timestamp Unix)
      
      // console.log('Verificando autenticação:', { 
      //   agoraUnix: agora, 
      //   expiracaoUnix: expiracaoUnix,
      //   tokenValido: agora < expiracaoUnix 
      // });

      return agora < expiracaoUnix;

    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      return false;
    }
  }
}

const authService = new AuthService();
export default authService;
